import type { InfiniteTablePropColumnSizing } from "@infinite-table/infinite-react";
import { useCallback, useEffect, useState } from "react";
import { DEFAULT_PANEL_MODE } from "@/components/mosaic/mosaicMenu/MosaicMenuItems";
import type { MonitorState } from "@/components/widgets/Monitor/type";
import type { WidgetScreenerState } from "@/components/widgets/Screener/type";
import { useTabs } from "@/context/tabs/TabsContext";
import { useWidgetView } from "@/context/WidgetViewContext";
import type { ViewId } from "@/shared/types/common";
import {
	freezeModeColumnSizing,
	justifyModeColumnSizing,
	NAME_FIELD,
	nameColumnSizing,
	TICKER_FIELD,
	tickerColumnSizing,
} from "./configs";
import { processNestedSubfields } from "./lib/helpers";

export const useColumnSizing = (widgetId: string) => {
	const { visibleColumns } = useWidgetView();
	const { getWidgetState } = useTabs();
	const widgetState = getWidgetState(widgetId as ViewId);
	const panelMode =
		(widgetState as MonitorState | WidgetScreenerState)?.panelMode ??
		DEFAULT_PANEL_MODE;

	const [columns, setColumns] = useState<string[]>([]);
	const [viewportReservedWidth, setViewportReservedWidth] = useState(0);
	const [columnSizing, setColumnSizing] =
		useState<InfiniteTablePropColumnSizing>({});

	const getColumnSizing = useCallback(
		(columnName: string) => {
			if (columnName === TICKER_FIELD) {
				return tickerColumnSizing;
			}
			if (columnName === NAME_FIELD) {
				return nameColumnSizing;
			}

			return panelMode === DEFAULT_PANEL_MODE
				? justifyModeColumnSizing
				: freezeModeColumnSizing;
		},
		[panelMode],
	);

	useEffect(() => {
		const newColumns: string[] = [];

		if (Array.isArray(visibleColumns[widgetId])) {
			for (const column of visibleColumns[widgetId]) {
				const columnName = column.name;
				if (column.subFields?.length) {
					processNestedSubfields(columnName, column.subFields, newColumns);
				} else {
					newColumns.push(columnName);
				}
			}
		}

		setColumns(newColumns);
	}, [visibleColumns[widgetId], widgetId]);

	const resetColumnSize = useCallback(() => {
		const newColumnSizing = Object.fromEntries(
			columns.map((name) => [name, getColumnSizing(name)]),
		) as InfiniteTablePropColumnSizing;

		setColumnSizing(newColumnSizing);
		setViewportReservedWidth(0);
	}, [columns, getColumnSizing]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: correct deps array
	useEffect(() => {
		resetColumnSize();
	}, [panelMode, widgetId]);

	return {
		panelName: panelMode,
		viewportReservedWidth,
		getColumnSizing,
		columnSizing,
		onColumnSizingChange: setColumnSizing,
		resetColumnSize,
	};
};
